import { Box, Container, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import cityData from "../data/topCities.json";
import slugify from "../utils/slugify";

// import lockIcon from "../assets/icons/lock.png";
import { getCityImage } from "../assets/images/city-image/cityImages";
import SEO from "../components/seo/SEO";
import ShareActions from "../components/common/ShareActions";

const Cities = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh", background: "#F6F8FB" }}>
      <>
        <SEO
          title="Explore Cities in India | NearbyGuide"
          description="Browse major cities in India to discover temples, hospitals, restaurants, malls and trusted nearby places. Explore city-wise guides on NearbyGuide."
          canonical="https://nearbyguide.in/city"
          image="https://nearbyguide.in/images/og-city.png"
          key="top cities in india, major cities in india, explore cities in india, city guide india, delhi city guide, mumbai city guide, kanpur city guide, lucknow city guide, pune city guide, varanasi city guide, best cities to explore in india, popular cities in india, city wise nearby places, city wise local guide, tourist cities in india, upcoming cities in india, indian city discovery platform, NearbyGuide cities"
        >
          {/* ===== SCHEMA ===== */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "Cities in India",
              itemListElement: cityData.top_cities
                .filter((c) => c.status === "active")
                .map((c, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  name: c.city,
                  url: `https://nearbyguide.in/city/${c.city.toLowerCase()}`,
                })),
            })}
          </script>
        </SEO>

        {/* REST OF PAGE */}
      </>

      {/* ===== HEADER ===== */}
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          textAlign: "center",
          background: "linear-gradient(180deg, #0D3B66 0%, #0B2E4A 100%)",
          color: "#fff",
          position:"relative"
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "1.9rem", md: "2.7rem" },
            fontWeight: 900,
            color: "#F4D35E",
            textShadow: "0 2px 6px rgba(0,0,0,0.55)",
          }}
        >
          Explore Cities
        </Typography>

        <Typography
          sx={{
            mt: 1,
            maxWidth: 620,
            mx: "auto",
            opacity: 0.9,
            fontSize: "0.95rem",
            color: "rgba(250,240,202,0.95)",
            textShadow: "0 1px 3px rgba(0,0,0,0.45)",
          }}
        >
          Discover trusted temples, hospitals, restaurants and landmarks city by
          city across India
        </Typography>
        <ShareActions
          title={`${cityData.name} | NearbyGuide`}
          text={`Discover trusted places and attractions in ${cityData.name}, ${cityData.state}.`}
        />
      </Box>

      {/* ===== CITY LIST ===== */}
      <Container sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 2.5, md: 3 },
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          {cityData.top_cities.map((c) => {
            const isActive = c.status === "active";

            return (
              <Box
                key={c.city}
                onClick={() => isActive && navigate(`/city/${slugify(c.city)}`)}
                sx={{
                  flex: {
                    xs: "1 1 100%",
                    sm: "1 1 calc(50% - 20px)",
                    md: "1 1 calc(25% - 24px)",
                    lg: "1 1 calc(20% - 24px)", // 5 cards desktop
                  },
                  maxWidth: {
                    xs: "100%",
                    sm: "calc(50% - 20px)",
                    md: "calc(25% - 24px)",
                    lg: "calc(20% - 24px)",
                  },
                  cursor: isActive ? "pointer" : "not-allowed",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: 280,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
                    transition: "all .35s ease",
                    "&:hover": isActive
                      ? {
                          transform: "translateY(-6px)",
                          boxShadow: "0 35px 80px rgba(0,0,0,0.28)",
                        }
                      : {},
                  }}
                >
                  {/* IMAGE */}
                  <Box
                    component="img"
                    src={getCityImage(c.city, c.status)}
                    alt={c.city}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: isActive ? "none" : "grayscale(1)",
                    }}
                  />

                  {/* OVERLAY */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.75))",
                    }}
                  />

                  {/* CITY NAME */}
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 18,
                      left: 18,
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "1.25rem",
                      zIndex: 2,
                    }}
                  >
                    {c.city}
                  </Typography>

                  {/* LOCK FOR COMING SOON */}
                  {!isActive && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 3,
                      }}
                    >
                      {/* <Box
                        component="img"
                        src={lockIcon}
                        alt="Coming soon"
                        sx={{ width: 46, mb: 0.8 }}
                      /> */}
                      {/* <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "0.75rem",
                          letterSpacing: 1,
                        }}
                      >
                        COMING SOON
                      </Typography> */}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default Cities;
